import { ChangesetConflictDTO, ChangesetResolutionDTO, ResolutionChoice, ResolutionChoices } from "../models/ChangeRequestDTO";

/**
 * The words the review uses for the two sides of a conflict. They depend on who reads: the author sees "Yours" against "Ana, now on
 * the page"; a reviewer sees "Ruben's proposal" against the same, and is told who decides.
 */
export interface SideLabels {
    /** The author's side: "Yours", or "Ruben's proposal". */
    ours: string;
    /** The page's side: "Ana, now on the page", or "Now on the page". */
    theirs: string;
    /** The choice buttons (the author's). */
    keepOurs: string;
    takeTheirs: string;
    /** For a reader who does not resolve: who does ("Ruben decides"). */
    decides?: string;
    /** "Resolved: …" by choice. */
    resolved: Record<ResolutionChoice, string>;
}

const possessive = (name: string) => (name === "you" ? "your" : `${name}'s`);

export function sideLabels(opts: { isAuthor: boolean; authorName?: string; editorName?: string }): SideLabels {
    const editor = opts.editorName;
    const theirs = editor ? `${editor}, now on the page` : "Now on the page";
    const takeTheirs = editor ? `Take ${possessive(editor)}` : "Take theirs";
    const theirsTaken = editor ? `${possessive(editor)} taken` : "the page's taken";
    if (opts.isAuthor) {
        return {
            ours: "Yours",
            theirs,
            keepOurs: "Keep yours",
            takeTheirs,
            resolved: { [ResolutionChoices.Ours]: "yours kept", [ResolutionChoices.Theirs]: theirsTaken, [ResolutionChoices.Manual]: "your edit" },
        };
    }
    const author = opts.authorName ?? "the author";
    return {
        ours: `${possessive(author).replace(/^the author's$/, "The author's")} proposal`,
        theirs,
        keepOurs: `Keep ${possessive(author)}`,
        takeTheirs,
        decides: `${author === "the author" ? "The author" : author} decides`,
        resolved: { [ResolutionChoices.Ours]: `${possessive(author)} kept`, [ResolutionChoices.Theirs]: theirsTaken, [ResolutionChoices.Manual]: `${possessive(author)} edit` },
    };
}

/** What an update settled, in words: "took the page's line 5 on “Use cases intro”". */
export function recapResolutions(resolutions: ChangesetResolutionDTO[], titleOf: (pageId: number) => string): string[] {
    return resolutions.map((r) => {
        const page = `“${titleOf(r.pageId)}”`;
        if (r.field === "page") return `deleted ${page} anyway`;
        if (r.field === "content" && r.line == null) return r.choice === ResolutionChoices.Theirs ? `kept the content of ${page}` : `cleared ${page} anyway`;
        const what = r.field === "title" ? `the title of ${page}` : r.field === "rules" ? `rule “${r.path}” of ${page}` : `line ${r.line} on ${page}`;
        const verb = r.choice === ResolutionChoices.Theirs ? "took the page's" : r.choice === ResolutionChoices.Manual ? "rewrote" : "kept the proposed";
        return `${verb} ${what}`;
    });
}

/** "A, B and C". */
export function listWords(words: string[]): string {
    if (words.length <= 1) return words[0] ?? "";
    return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

/** A reject reason that names the conflicts, for a reviewer who will not wait. */
export function conflictsReason(conflicts: ChangesetConflictDTO[], titleOf: (pageId: number) => string): string {
    const byPage = new Map<number, string[]>();
    for (const c of conflicts) {
        const where = c.field === "title" ? "title" : c.field === "rules" ? `rule “${c.path}”` : c.field === "content" && c.line ? `line ${c.line}` : c.field === "page" ? "the page itself" : "content";
        byPage.set(c.pageId, [...(byPage.get(c.pageId) ?? []), where]);
    }
    const pages = Array.from(byPage.entries()).map(([id, wheres]) => `“${titleOf(id)}” (${wheres.join(", ")})`);
    return `Conflicts with edits made since the proposal: ${pages.join("; ")}. Please update the change request and resolve them.`;
}
